import { Component, Input, OnInit } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import { PhotoService, UserPhoto } from '../services/photo.service';
import { BehaviorSubject, using } from 'rxjs';
import { GameUser } from 'infrastructure/lib/src/models/game-user';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'environment';
import { MemoryViewerComponent } from '../memory-viewer/memory-viewer.component';
import MainScene from '../Scenes/MainScene';
import BaseLevelScene from '../Scenes/BaseLevelScene';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth';
import { SocialGameUser } from '../models/social-game-user';
import { GlobalService } from '../services/global.service';
import { SendMessageModalComponent } from '../send-message-modal/send-message-modal.component';
import { AudioService } from '../services/audio.service';
import { AudioViewerComponent } from '../audio-viewer/audio-viewer.component';
import { MixTape } from 'src/classes/MixTape';
import { UserAudio } from 'infrastructure/lib/src/models/user-audio';

@Component({
  selector: 'app-profile-viewer',
  templateUrl: './profile-viewer.component.html',
  styleUrls: ['./profile-viewer.component.scss'],
})
export class ProfileViewerComponent  implements OnInit {
  @Input() username: string;
  scene: MainScene | BaseLevelScene;
  photos$ = new BehaviorSubject<UserPhoto[]>([]);
  audio$ = AudioService.audio$;
  gameUser$ = new BehaviorSubject<SocialGameUser>({
    username: "",
    userId: "",
    bio: "",
    gameItems: [],
    inventory: {},
    type: "GameUser",
    isTrailing: [],
    isTrailedBy: [],
  });
  session: AuthSession;

  constructor(
    private modalCtrl: ModalController,
    private http: HttpClient,
    private toastController: ToastController,
    public globalService: GlobalService,
  ) { 
  }

  async ngOnInit() {
    this.session = await fetchAuthSession();
    console.log('profile-viewer-component ngOnInit');
    console.log(this.username);
    const imagesByUser = await PhotoService.getImagesByUser(this.username);
    const audioByUser = await AudioService.getAudiosByUser(this.username);
    console.log(imagesByUser);
    this.photos$.next(imagesByUser);
    this.audio$.next(audioByUser);
    this.getGameUserInfo(this.username).subscribe({
      next: data => {
        console.log("getGameUserInfo data:");
        console.log(data);
        // Extend the GameUser to include trailmates
        const socialGameUser: SocialGameUser = {
          ...(data["userData"] as GameUser),
          isTrailing: data["isTrailing"] as string[],
          isTrailedBy: data["isTrailedBy"] as string [],
        } as SocialGameUser;
        this.gameUser$.next(socialGameUser);
      }
    });
  }

  public getGameUserInfo(username: string){
    return this.http.get(`${environment.REST_API}/users?username=${username}`);//  as Observable<GameUser>;
  } 

  photoClicked(photo: UserPhoto){
    MemoryViewerComponent.openModal(photo.filepath, this.scene, false);
  }
  
  audioClicked(userAudio: UserAudio){
    AudioViewerComponent.openModal(userAudio, this.scene as (MainScene));
  }

  cancel() {
    this.modalCtrl.dismiss(null, 'dismiss');
  }

  trailUser(){
    console.log("trail user clicked");
    this.http.post(`${environment.REST_API}/trailmates`,
      {username: this.username},
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.session.tokens.idToken}`
        }),
        }
    ).subscribe({
      next: async data => {
        console.log(data);
        const toast = await this.toastController.create({
          message: `You are now trailing ${this.username}!`,
          duration: 2000,
          color: 'success',
          position: 'top',
        });
        await toast.present();
      },
      error: async error => {
          console.error(`There was an error trailing ${this.username}.`, error);

          const toast = await this.toastController.create({
            message: error.error.message,
            duration: 2000,
            color: 'warning',
            position: 'top',
          });
          await toast.present();
      }
    })
  }

  // Gets the users the selected user is trailing.
  getUsersUserIsTrailing(){
    return this.gameUser$.value.isTrailing;
  }

  // Gets the users that are trailing the selected user.
  getUsersTrailingUser(){
    return this.gameUser$.value.isTrailedBy;
  }

  getUsersMixTapes(){
    return this.audio$.value;
  }

  calculateAge(birthdate: string): number {
    if (birthdate) {
      const birthDate = new Date(birthdate);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return age;
    }
    return 0;
  }

  getGenderIcon(value: string | null): string {
    switch (value) {
      case 'male': return 'male';
      case 'female': return 'female';
      case 'non-binary': return 'male-female-outline';
      case 'trans': return 'transgender';
      case 'unknown': return 'help';
      default: return 'help-circle-outline';
    }
  }

  async openProfileModal(username: string){
    console.log(`viewing ${username}'s profile`);
    await this.modalCtrl.dismiss();
    this.modalCtrl.create({
      component: ProfileViewerComponent,
      componentProps: { 
        username: username,
        scene: null
      }
    }).then((modal) => {
      console.log("Presenting the profile modal");
      modal.present();
      GlobalService.incrementModalCount();
      modal.onDidDismiss().then(() => {
        GlobalService.decrementModalCount();
      });
    });
  }

  openSendMessageModal(recipient: string){
    GlobalService.incrementModalCount();
    this.modalCtrl.create({
      component: SendMessageModalComponent,
      componentProps: {
        recipient: recipient
      }
    }).then((modal) => {
      modal.present();
      modal.onDidDismiss().then(() => {
        GlobalService.decrementModalCount();
      });
    });
  }

}
